/* eslint-disable no-param-reassign */
/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TFunction } from 'i18next';
import { AppThunk } from '../../../app/store';
import Repos from '../../../infrastructure/repositories/Repos';
import { markExternalRedirectStarted, getFlowSnapshot } from '../../../app/shared/utils/popup.util';
import { getConfig } from '../../../app/shared/utils/config.util';
import { IGetListQueryParams } from '../../../infrastructure/repositories/utils/IGetListQueryParams';
import { ILoaIntegration } from '../../../domain/models/ILoaIntegration';
import { showToast } from '../../../app/shared/AppSlice/AppSlice';
import { I18nNamespaces } from '../../../app/shared/utils/constants.util';

interface LoaIntegrationsState {
  providers: ILoaIntegration[];
  loading: boolean;
  error: string | null;
}

const initialState: LoaIntegrationsState = {
  providers: [],
  loading: false,
  error: null
};

export const loaIntegrationsSlice = createSlice({
  name: 'loaIntegrations',
  initialState,
  reducers: {
    setProviders(state, action: PayloadAction<ILoaIntegration[]>) {
      state.providers = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    resetSetupSecurityState: (): LoaIntegrationsState => initialState
  }
});

export const {
  setProviders,
  setLoading,
  setError,
  resetSetupSecurityState
} = loaIntegrationsSlice.actions;

export const fetchProvidersByLevel = (level: number): AppThunk<Promise<ILoaIntegration[]>> => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const queryParams: IGetListQueryParams<ILoaIntegration> = {
      filter: { level }
    };

    const res = await Repos.instance.loaIntegrationsRepository.getAll(queryParams);
    const items = (res?.items ?? []) as ILoaIntegration[];
    dispatch(setProviders(items));
    return items;
  } catch (e) {
    dispatch(setError('loaIntegrations.errors.providersFetchFailed'));
    throw e;
  } finally {
    dispatch(setLoading(false));
  }
};

export function buildLoaNavigateUrl(providerId: string): string {
  const { iamConfig } = getConfig();
  const base = iamConfig.apiBaseUrl.replace(/\/$/, '');
  const {
    channelId, origin, applicationId, relativePath, mode, instance,
    resourceName
  } = getFlowSnapshot();

  const navigateBase = `${base}/loaIntegrations/${providerId}/navigate`;

  const params = new URLSearchParams();

  // Identifies the frontend framework/flow to the backend. iam-profile-app is the
  // legacy flow, so it always reports 'v1'.
  params.append('flowVersion', 'v1');

  if (channelId) params.append('channelId', channelId);
  if (origin) params.append('origin', origin);
  if (applicationId) params.append('applicationId', applicationId);
  if (mode) params.append('mode', mode);
  if (relativePath) params.append('relativePath', encodeURIComponent(relativePath));
  if (instance) params.append('instance', instance);
  if (resourceName) params.append('resourceName', resourceName);

  const queryString = params.toString();
  return queryString ? `${navigateBase}?${queryString}` : navigateBase;
}

export const navigateDefaultProviderForLevel = (level: number, t: TFunction): AppThunk => async (dispatch) => {
  try {
    const providers = await dispatch(fetchProvidersByLevel(level) as any) as ILoaIntegration[];
    const provider = providers[0];

    if (!provider) {
      dispatch(
        showToast({
          title: t('loaEnrichment.common.errors.noProviderForLevelToast', { level, ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        })
      );
      return;
    }

    const url = buildLoaNavigateUrl(provider.id);

    markExternalRedirectStarted();
    window.location.assign(url);
  } catch {
    dispatch(
      showToast({
        title: t('loaEnrichment.common.errors.navigateRequestFailedToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      })
    );
  }
};

export default loaIntegrationsSlice.reducer;
